import React, { useEffect, useRef } from 'react';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Breadcrumb from 'tbk-components/src/components/Breadcrumb';
import AnimatedArcs from '../AnimatedArcs';
import { useTranslation } from 'react-i18next';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import cn from 'classnames';

export interface ICPTHeroProps extends IBaseElementProps {
	postTags?: ILink[];
	headline?: string;
	postTitle?: string;
	excerpt?: string;
	eventDate?: string;
	eventLocation?: string;
	ctaButtons?: ILink[];
	mediaPosition?: 'split' | 'wallpaper';
	image?: IImage;
	hideBreadcrumbs?: boolean;
	breadcrumbs?: ILink[];
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	minReadLabel?: string;
}

const CPTHero: React.FC<ICPTHeroProps> = ({
	id,
	className,
	classNames = [],
	postTags,
	headline,
	postTitle,
	excerpt,
	eventDate,
	eventLocation,
	ctaButtons,
	mediaPosition = 'split',
	image,
	hideBreadcrumbs,
	breadcrumbs,
	lineAnimationDelay,
	lineAnimationOffset,
	minReadLabel,
}) => {
	const { t } = useTranslation();
	const ref = useRef(null);

	useEffect(() => {
		const estimateRead = () => {
			if (!ref.current) {
				return;
			}

			const main = document.querySelector('#main') as HTMLElement;
			const textArr = main.innerText.split(' ');
			const wordPerMin = 225;
			const estimateRead = Math.ceil(textArr.length / wordPerMin);
			const readLabel = minReadLabel || t(' min read');
			ref.current.innerText = `${estimateRead} ${readLabel}`;
		};

		window.addEventListener('load', estimateRead);

		return () => {
			window.removeEventListener('load', estimateRead);
		};
	}, []);

	return (
		<section
			id={id}
			className={
				className ||
				cn(
					`cpt-hero__container cpt-hero__container-media-${
						image?.src ? mediaPosition : 'no-image'
					}`,
					...classNames,
				)
			}
		>
			<div className="cpt-hero__container-inner">
				<div className="cpt-hero__left">
					<div className="cpt-hero__left-inner">
						{postTags?.length > 0 && (
							<div className="cpt-hero__tags">
								{postTags.map((postTag, index) => {
									return (
										<Button
											key={index}
											{...postTag}
											className="cpt-hero__tag"
										/>
									);
								})}
							</div>
						)}
						{headline && (
							<span className="cpt-hero__headline">
								{headline}
							</span>
						)}
						{postTitle && <h1>{postTitle}</h1>}
						{excerpt && (
							<p className="cpt-hero__excerpt">{excerpt}</p>
						)}
						{(eventDate || eventLocation) && (
							<div className="cpt-hero__event-info">
								{eventDate && <span>{eventDate}</span>}
								{eventLocation && <span>{eventLocation}</span>}
							</div>
						)}
						{ctaButtons?.length > 0 && (
							<div className="cpt-hero__cta">
								{ctaButtons.map((ctaBtn, index) => {
									return (
										<Button
											key={index}
											{...ctaBtn}
											classNames={[
												index === 1
													? 'btn-secondary'
													: '',
											]}
										/>
									);
								})}
								<span
									className="cpt-hero__estimate-read"
									ref={ref}
								></span>
							</div>
						)}
						{!hideBreadcrumbs && breadcrumbs.length > 0 && (
							<div className="cpt-hero__breadcrumbs">
								<Breadcrumb links={breadcrumbs} />
							</div>
						)}
					</div>
				</div>
				{image && (
					<div className="cpt-hero__right">
						<div className="cpt-hero__right-inner">
							<div className="cpt-hero__image">
								<Image {...image} />
							</div>
						</div>
					</div>
				)}
			</div>
			<AnimatedArcs
				arcsStyle={'Component - Two Lines'}
				arcsPosition="Right"
				arcOneColor={
					image?.src ? 'var(--bs-primary)' : 'var(--bs-light)'
				}
				arcOneStrokeWidth={3}
				arcTwoColor="var(--bs-white)"
				arcTwoStrokeWidth={3}
				animationDelay={lineAnimationDelay}
				animationOffset={lineAnimationOffset}
			/>
		</section>
	);
};

export default CPTHero;
